import React, { useState } from 'react';
import { BLOCKS } from '@contentful/rich-text-types';

import MultiHeroBanner from '../../UI/Banners/MultiHeroBanner/MultiHeroBanner';
import BladeContainer from '../../UI/BladeContainer/BladeContainer';
import EmailSignUpForm from '../../EmailSignUpForm/EmailSignupForm';
import RichText from '../../UI/RichText';
import LegalText from '../../UI/LegalText/LegalText';
import Seo from '../../Seo/Seo';
import useLocation from '../../../utils/location';
import StoreLocationSearch from '../../StoreLocationSearch';
import isKiosk from '../../../utils/isKiosk';
import extractArrayData from '../../../utils/extractArrayData';
import { Contentful } from '../../../types';
import {
  ContentfulPage,
} from '../../../ContentfulTypes';

import {
  PageWrapper, Wrapper, Container, NoBannerWrapper, NoBannerTitle, NoBannerText,
} from './styles';

interface Props {
  contentfulData: ContentfulPage;
  locale?: string;
  preferredProvince?: string;
}

// Elements to render on rich text
const headlineOptions = {
  renderNode: {
    [BLOCKS.PARAGRAPH]: (_, children) => (
      <NoBannerTitle>{children}</NoBannerTitle>
    ),
    [BLOCKS.HEADING_1]: (_, children) => (
      <NoBannerTitle>{children}</NoBannerTitle>
    ),
    [BLOCKS.HEADING_2]: (_, children) => (
      <NoBannerTitle>{children}</NoBannerTitle>
    ),
  },
};
const bodyOptions = {
  renderNode: {
    [BLOCKS.PARAGRAPH]: (_, children) => (
      <NoBannerText>{children}</NoBannerText>
    ),
    [BLOCKS.HEADING_1]: (_, children) => (
      <NoBannerText>{children}</NoBannerText>
    ),
    [BLOCKS.HEADING_2]: (_, children) => (
      <NoBannerText>{children}</NoBannerText>
    ),
    [BLOCKS.HEADING_3]: (_, children) => (
      <NoBannerText>{children}</NoBannerText>
    ),
  },
};

const PromoScreen = ({
  locale,
  contentfulData,
  preferredProvince,
}: Props) => {
  const isKioskMode = isKiosk();

  // State for email sign up blade
  const [bladeVisible, setBladeVisible] = useState(false);

  // Get email sign up blade
  const emailBlade = extractArrayData(
    contentfulData.pageSections,
    Contentful.ContentfulId,
    '2SvFPTgSQY1uuyWMLLWg89',
  );

  // Find page section that is "no promos" text
  const noPromoData = extractArrayData(
    contentfulData.pageSections,
    Contentful.ContentfulId,
    '48GUaUUolxFlVQnhWhTAgM',
  );

  // Separate our multi hero banners for promos from other sections, and only include provincial matches
  const multiHeroBanners = contentfulData.pageSections
    .filter((section) => section.__typename === 'ContentfulMultiHeroBanner')
    .filter((banner) => banner?.bannerDisplayProvinces?.includes(preferredProvince));

  // Get legal text from each multi hero banner that displays
  const multiHeroBannersLegalText = multiHeroBanners.map((banner) => banner.bannerLegalText);

  // Separate our promo banners from other sections, and only include provincial matches
  const promoBanners = contentfulData.pageSections
    .filter((section) => section.__typename === 'ContentfulBanner')
    .filter((banner) => banner?.bannerDisplayProvinces?.includes(preferredProvince));

  // Get legal text from each banner that displays
  const bannerLegalText = promoBanners.map((banner) => banner.bannerLegalText);

  // Get the contact legal details
  const legalContactSection = extractArrayData(
    contentfulData.pageSections,
    Contentful.ContentfulId,
    isKioskMode ? '4q31HyAjDE3z3Xp24lnxjz' : '1RrcRZtho94mjQsVt0vOmh',
  )?.contentBody;

  // Add the contact legal details to the other multi hero banners for promo banners legal
  multiHeroBannersLegalText?.push(legalContactSection);

  // Add the contact legal details to the other promo banners legal
  bannerLegalText?.push(legalContactSection);

  // Locations and logos section
  const locationSection = extractArrayData(
    contentfulData.pageSections,
    Contentful.ContentfulId,
    '5EBumf8jN2kH4d8R9Yl0RY',
  )?.combinatorSectionItems;

  const { baseUrl, homePage, currentPage } = useLocation();

  const EventsSchema = promoBanners.map((banner) => {
    const image = banner?.foregroundImageDesktop?.asset?.localImage?.publicURL
      ? `${baseUrl}${banner.foregroundImageDesktop.asset.localImage.publicURL}`
      : '';
    let url = banner?.bannerCta?.ctaUrl ?? '';
    if (/^\//.exec(url)) url = `${baseUrl}${url}`;

    return {
      '@context': 'https://schema.org',
      '@type': 'Event',
      name: banner?.foregroundImageDesktop?.altText ?? '',
      description: banner?.bannerCta?.ctaLabel?.ctaLabel ?? '',
      image,
      startDate: banner?.promotionStartDate ?? banner?.createdAt ?? '',
      endDate: banner?.promotionEndDate ?? '',
      eventStatus: 'https://schema.org/EventScheduled',
      eventAttendanceMode: 'https://schema.org/OnlineEventAttendanceMode',
      location: {
        '@type': 'VirtualLocation',
        url,
      },
    };
  });

  const schema = [
    {
      '@context': 'https://schema.org/',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Home',
          item: homePage,
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: 'Promotion',
          item: currentPage,
        },
      ],
    },
    ...EventsSchema,
  ];

  const emailBladeProps = {
    heading: emailBlade.bladeSections[0],
    formFields: emailBlade.bladeSections[1].formFields,
    cta: emailBlade.bladeSections[2],
    successSection: emailBlade.bladeSections[3],
    errorSection: emailBlade.bladeSections[6],
  };
  return (
    <PageWrapper>
      <Seo
        title={contentfulData.metaTitle}
        description={contentfulData?.metaDescription?.metaDescription}
        ldJson={schema}
      />

      {multiHeroBanners.length > 0 ? (
        multiHeroBanners.map((banner, index) => banner.bannerCta?.ctaTarget === 'function' ? (
          <Wrapper key={banner.id}>
            <Container>
              <MultiHeroBanner
                data={banner}
                bladeVisible={bladeVisible}
                setBladeVisible={setBladeVisible}
                position={index}
              />
            </Container>
          </Wrapper>
        ) : (
          <Wrapper key={banner.id}>
            <Container>
              <MultiHeroBanner
                data={banner}
                position={index}
              />
            </Container>
          </Wrapper>
        ))
      ) : (
        <NoBannerWrapper tabIndex="0">
          <RichText document={noPromoData?.heading} options={headlineOptions} />
          <RichText document={noPromoData?.subHeading} options={bodyOptions} />
        </NoBannerWrapper>
      )}

      {/* Legal text array for banners */}
      {multiHeroBannersLegalText.length > 0 && (
      <LegalText preExpand legalText={multiHeroBannersLegalText} />
      )}

      {/* Email sign up form blade */}
      <BladeContainer
        bladeState={bladeVisible}
        setBladeState={setBladeVisible}
        bladeWidth={767}
        locale={locale}
        addDivider
      >
        <EmailSignUpForm {...emailBladeProps} />
      </BladeContainer>
      <StoreLocationSearch data={locationSection} locale={locale} />
    </PageWrapper>
  );
};

export default PromoScreen;
